/**
 * Cross-domain `_zm_visitor_guid` sync (zoom.us ↔ zoom.com ↔ zoomdev.us).
 *
 * zoom.us and zoom.com each mint their own `_zm_visitor_guid`, and the browser will not let
 * either domain read the other's cookie. The value is therefore handed over in the URL on
 * cross-domain navigation:
 *
 *   Inbound  — adopt the GUID arriving in the query string onto this domain's cookie, then
 *              immediately strip the param with `history.replaceState` (no reload) so the GUID
 *              cannot be read out of the address bar, bookmarked, or copied and sent to anyone.
 *   Outbound — append the param to cross-domain links at *activation* time only. Nothing is
 *              written into the DOM on page load, and right-click is deliberately ignored so
 *              "Copy link address" never produces a URL carrying this visitor's GUID.
 *
 * Deliberately self-contained (own cookie reader/writer, no imports) so it can run at module
 * evaluation time, before any component decides to mint a fresh GUID.
 */

const VISITOR_GUID_COOKIE = '_zm_visitor_guid'
export const VISITOR_GUID_PARAM = 'visitorGuid'
const VISITOR_GUID_PARAMS = [VISITOR_GUID_PARAM, '_zm_visitor_guid', 'vguid']
const SYNC_DOMAINS = ['zoom.com', 'zoom.us', 'zoomdev.us']
const COOKIE_MAX_AGE_HOURS = 24

/**
 * Accepts 32 hex chars (the format `generateId()` produces) or a dashed UUID. Rejects '', 'false',
 * 'undefined', and anything hand-crafted, so a tampered URL cannot graft an arbitrary value in.
 */
export function isValidVisitorGuid(value) {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(value)
  )
}

/**
 * Reads a cookie without the JSON coercion in `utilities.getCookie` — an all-digit GUID is valid
 * JSON, so that helper would return it as a Number and drop any leading zeros.
 */
function readRawCookie(name) {
  try {
    const prefix = name + '='
    const parts = document.cookie ? document.cookie.split(';') : []
    for (let i = 0; i < parts.length; i++) {
      const cookie = parts[i].trim()
      if (cookie.indexOf(prefix) === 0) return decodeURIComponent(cookie.substring(prefix.length))
    }
  } catch (_) { }
  return null
}

/** The allowlisted registrable domain a hostname belongs to, or null (localhost, previews, 3rd parties). */
function registrableDomain(hostname) {
  const host = (hostname || '').toLowerCase()
  for (let i = 0; i < SYNC_DOMAINS.length; i++) {
    const domain = SYNC_DOMAINS[i]
    if (host === domain || host.endsWith('.' + domain)) return domain
  }
  return null
}

function isGuidRecipientHost(hostname) {
  const host = (hostname || '').toLowerCase()
  const domain = registrableDomain(host)
  if (!domain) return false
  return host === domain || host === 'www.' + domain
}

function writeVisitorGuidCookie(guid) {
  const expires = new Date(Date.now() + COOKIE_MAX_AGE_HOURS * 60 * 60 * 1000).toUTCString()
  const domain = registrableDomain(location.hostname)
  if (domain) {
    // Apex-scoped so every subdomain (www, stage, support, …) shares the one value.
    document.cookie =
      VISITOR_GUID_COOKIE + '=' + guid + '; expires=' + expires + '; path=/;secure;samesite=none;domain=.' + domain
  } else {
    // localhost / preview hosts: no registrable domain to scope to, and `secure` would be
    // rejected over plain http — keep it host-only so local QA still works.
    document.cookie = VISITOR_GUID_COOKIE + '=' + guid + '; expires=' + expires + '; path=/'
  }
}

/** Removes every accepted param — valid or not — from the address bar without reloading. */
function stripVisitorGuidFromUrl() {
  try {
    const url = new URL(location.href)
    let stripped = false
    for (let i = 0; i < VISITOR_GUID_PARAMS.length; i++) {
      if (url.searchParams.has(VISITOR_GUID_PARAMS[i])) {
        url.searchParams.delete(VISITOR_GUID_PARAMS[i])
        stripped = true
      }
    }
    if (!stripped) return
    const query = url.searchParams.toString()
    // replaceState rather than pushState: Back must not navigate to the decorated URL.
    history.replaceState(
      history.state,
      document.title,
      url.pathname + (query ? '?' + query : '') + url.hash
    )
  } catch (_) { }
}

/**
 * Inbound half. Adopts the GUID from the URL onto this domain's cookie, then strips the param.
 * The strip runs unconditionally, so an invalid or forged param is still removed from the URL.
 *
 * @returns {string|null} The adopted GUID, or null when nothing usable arrived.
 */
export function syncVisitorGuidFromUrl() {
  let incoming = null
  try {
    const params = new URLSearchParams(location.search)
    for (let i = 0; i < VISITOR_GUID_PARAMS.length; i++) {
      const raw = (params.get(VISITOR_GUID_PARAMS[i]) || '').trim()
      if (isValidVisitorGuid(raw)) {
        incoming = raw
        break
      }
    }
  } catch (_) { }

  stripVisitorGuidFromUrl()

  if (!incoming) return null
  // Already in sync — skip the write so a matching GUID is left untouched.
  if (readRawCookie(VISITOR_GUID_COOKIE) === incoming) return incoming
  writeVisitorGuidCookie(incoming)
  return incoming
}

/**
 * Outbound half. Stamps this domain's GUID onto a cross-domain link as it is activated.
 *
 * `pointerdown` limited to buttons 0 and 1 covers left-click, middle-click (open in new tab) and
 * ⌘/Ctrl-click; `keydown` Enter covers keyboard activation. Right-click (button 2) is intentionally
 * *not* handled: "Copy link address" and "Open in new tab" from the context menu must yield a clean
 * URL, otherwise a pasted link would hand this visitor's GUID to whoever opens it.
 */
function decorateVisitorGuidLink(event) {
  if (event.type === 'pointerdown' && event.button !== 0 && event.button !== 1) return
  if (event.type === 'keydown' && event.key !== 'Enter') return

  try {
    // composedPath so links inside shadow roots resolve to the real anchor, not the host element.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target]
    let anchor = null
    for (let i = 0; i < path.length; i++) {
      const node = path[i]
      if (node && node.tagName === 'A' && node.getAttribute('href')) {
        anchor = node
        break
      }
      if (node === document) break
    }
    if (!anchor) return

    const url = new URL(anchor.href, location.href)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return

    const target = registrableDomain(url.hostname)
    // Skip non-allowlisted hosts, and skip same-domain links — those already share the cookie.
    if (!target || target === registrableDomain(location.hostname)) return
    // Skip subdomain microsites (investors, news, careers, …) even on an allowlisted domain.
    if (!isGuidRecipientHost(url.hostname)) return

    const guid = readRawCookie(VISITOR_GUID_COOKIE)
    if (!isValidVisitorGuid(guid)) return
    if (url.searchParams.get(VISITOR_GUID_PARAM) === guid) return // idempotent across re-activations

    url.searchParams.set(VISITOR_GUID_PARAM, guid)
    anchor.href = url.toString()
  } catch (_) { }
}

let linkDecorationBound = false

/** Binds outbound link decoration once, in the capture phase so `stopPropagation` cannot defeat it. */
export function initVisitorGuidLinkDecoration() {
  if (linkDecorationBound) return
  linkDecorationBound = true
  document.addEventListener('pointerdown', decorateVisitorGuidLink, true)
  document.addEventListener('keydown', decorateVisitorGuidLink, true)
}

/** Both halves. Safe to call more than once. */
export function initVisitorGuidSync() {
  // Check feature toggle (follows SyncStatusMiddleware pattern)
  const isEnabled = document.body && document.body.dataset && document.body.dataset.visitorGuidSync === 'true'
  if (!isEnabled) return

  syncVisitorGuidFromUrl()
  initVisitorGuidLinkDecoration()
}
